import { print } from "graphql/language/printer";
import pluralize from "pluralize";
import { sortBy } from "ramda";

import type WebApiClient from "../../api/WebApiClient.js";
import { getClientSetup } from "../../app-utils/browser-compatibility.js";
import { saveAsPicture } from "../../app-utils/export.js";
import { type IGffPayload, type IGffTrackType, type IVcfPayload } from "../../ui-modules/core/TableExportDialog.js";
import { type IAppState } from "../reducer.js";
import { type ReduxAction, createReducer, type ReduxDispatch } from "../redux-utils.js";
import { type IAuditLogPayload } from "./auth.js";
import { loggedErrors } from "./errors.js";
import {
  type ITableData,
  buildDataTableQueryParamsForSpecificColumns,
  getSortOrderExpressionFromTableColumns,
} from "./table-data.js";
import { type IDataTableQueryParams, type IDownloadFileParams, type ISequenceExportInput } from "@/api/WebApiClient.ts";
import { type IQueryOptions, getDataTableQueryOptions } from "@/api/queries.ts";
import { createErrorHandler } from "@/app-utils/errors.ts";
import { getFilterExpression } from "@/app-utils/filter.ts";
import { type TGridSelModelIdentifier } from "@/app-utils/selection-model.ts";
import { capitalizeFirstLetter, pluralizeWord } from "@/app-utils/string.ts";
import { type Column, type InputRegionInput, type InputTableQuerySpec, type PlaybookEvent } from "@/graphql.ts";
import { BEGIN_EXPORT_CHART, DOWNLOAD_FILE_FAILED, END_EXPORT_CHART } from "@/redux/action-types.ts";
import {
  DATATABLE_PARENT_TYPE,
  ENTITY_TYPE,
  EXPORT_FILE_FORMATS,
  FEATURE_DISPLAY_NAME,
  LOG_ACTION,
  MAX_SEQUENCE_EXPORT_LENGTH,
  getSequenceFeatureTypeDisplayName,
} from "@/settings/settings.ts";
import { SelectionType, SelectionType2String } from "@/ui-modules/core/save-dialog/utils.ts";

export type IGenericExportPayload = IVcfPayload | IGffPayload | IDataExportInput;

/**
 * This module deals with data exports, that is, plain text/gda and image exports.
 * It controls the corresponding section dataExport:IDataExport in IAppState which is defined in reducer.ts
 *
 * not yet finished. Adaptations for GDA exports required.
 */

interface IExportAsFile {
  extension?: string;
  name?: string;
}

export interface IDownloadFileInput {
  format?: string;
  id?: number;
  path?: string;
  name?: string;
  trackTypes?: IGffTrackType[];
  withSequence?: boolean;
  useChromosomeName?: boolean;
  orgId?: number;
  compressed?: boolean;
  reverseComplement?: boolean;
  region?: InputRegionInput;
}

export interface IDataExport {
  shouldExportChart: boolean;
  exportError: string;
  exportChartFileName?: string;
}

const initialState: IDataExport = {
  shouldExportChart: false,
  exportError: null,
  exportChartFileName: null,
};

/**
 * Reducers
 */
export default createReducer(initialState, {
  [BEGIN_EXPORT_CHART]: (state: IDataExport, fileName: string): IDataExport => {
    return {
      ...state,
      exportChartFileName: fileName,
      shouldExportChart: true,
      exportError: null,
    };
  },
  [END_EXPORT_CHART]: (state: IDataExport, error?: string): IDataExport => {
    return {
      ...state,
      exportChartFileName: null,
      shouldExportChart: false,
      exportError: error,
    };
  },
});

/**
 * Action creators
 */
export function beginExportChartData(fileName: string) {
  return { type: BEGIN_EXPORT_CHART, payload: fileName };
}

export function endExportChartData(error?: string) {
  return { type: END_EXPORT_CHART, payload: error };
}

export function exportAsPicture(canvasRef: any, name: string) {
  return (dispatch) => {
    dispatch(auditExportFromPixelator({ name }));
    saveAsPicture(canvasRef, name);
  };
}

// this is graphQL entity but not available from rootQuery or rootMutation, since se use a REST endpoint
export interface ITableDownloadFileParams extends IDownloadFileParams {
  tableQuerySpec: InputTableQuerySpec;
  columnFields?: string[];
  columnTitles?: string[];
  columnTitleGroups?: string[];
  entityType: string;
  selectedIds: number[];
  request: string; // see SEL5-1483 and comment below in the table export method
  rowCount?: number;
}

export interface IDataExportInput {
  entityName?: string;
  format: string;
  selectionType: number;
  table: ITableData;
  region: InputRegionInput;
  summaryPageEntity: any;
  toClipboard: boolean;
  filename?: string;
  fastaHeaderTemplate?: string;
  compressed: boolean;
  reverseComplement: boolean;
}

function getRowCount(dataExportInput: IDataExportInput): number {
  return dataExportInput.selectionType === SelectionType.SelectedRows
    ? dataExportInput.table.selectionModel.selectedIds.length
    : dataExportInput.table.filteredRowCount;
}

function getRequest(visibleColumns: Column[], tableQuerySpec: InputTableQuerySpec): string {
  // SEL5-1483 : for improving the export performance we need to provide the graphQL request for getting the
  // table content as a stringified object { request, variables }
  const queryParams: IDataTableQueryParams = Object.assign(
    buildDataTableQueryParamsForSpecificColumns(tableQuerySpec, visibleColumns),
    // PLEASE DO NOT CHANGE OR EXTEND THE FOLLOWING PROPERTIES WITH COLUMN IDENTIFIERS -
    // THESE ARE THOSE PROPERTIES OF A GRID REQUEST THAT ARE NOT COLUMNS
    {
      withColumns: false,
      withRowIds: false,
      withPageRows: true,
      withPagination: false,
    },
  );
  const tableQueryOptions: IQueryOptions = getDataTableQueryOptions(queryParams, visibleColumns);
  return print(tableQueryOptions.query);
}

function getTableQuerySpec(dataExportInput: IDataExportInput, tableSpec: InputTableQuerySpec): InputTableQuerySpec {
  let sortOrder = getSortOrderExpressionFromTableColumns(dataExportInput.table.columns);
  if (
    tableSpec.queryType === DATATABLE_PARENT_TYPE.ORGANISM &&
    tableSpec.resultType !== ENTITY_TYPE.VARIANT &&
    tableSpec.resultType !== ENTITY_TYPE.AAFEATURE &&
    tableSpec.resultType !== ENTITY_TYPE.NUFEATURE &&
    tableSpec.resultType !== ENTITY_TYPE.COORD_LIST
  ) {
    sortOrder = "region.contig.sequenceId,region.range.start,region.range.stop";
  }

  return {
    ...tableSpec,
    rowFilter: getFilterExpression(dataExportInput.table.columns),
    rowSorting: sortOrder,
  };
}

function getVisibleColumns(dataExportInput: IDataExportInput): Column[] {
  return dataExportInput.table.columns.filter((c) => !c.hidden);
}

export function exportExcelFile(dataExportInput: IDataExportInput) {
  const tableSpec: InputTableQuerySpec = dataExportInput.table.tableQuerySpec;
  const tableQuerySpec: InputTableQuerySpec = getTableQuerySpec(dataExportInput, tableSpec);

  const visibleColumns: Column[] = getVisibleColumns(dataExportInput);
  const request: any = getRequest(visibleColumns, tableQuerySpec);

  const defaultFileName: string = capitalizeFirstLetter(
    dataExportInput.entityName
      ? dataExportInput.entityName
      : pluralize.plural(getSequenceFeatureTypeDisplayName(tableSpec).replace(/\s+/g, "_")),
  );

  const fileName = dataExportInput.filename ? dataExportInput.filename : defaultFileName;

  // TQS TODO refactor this input object
  const exportPayload: ITableDownloadFileParams = {
    id: tableSpec.id,
    name: fileName,
    extension: dataExportInput.format,
    entityType: tableSpec.resultType,
    columnFields: visibleColumns.map((c) => c.field),
    columnTitles: visibleColumns.map((c) => c.name),
    columnTitleGroups: visibleColumns.map((c) => c.groupName),
    selectedIds:
      dataExportInput.selectionType === SelectionType.SelectedRows
        ? (getSortedSelectedIds(dataExportInput.table) as number[])
        : null,
    tableQuerySpec,
    rowCount: getRowCount(dataExportInput),
    request: request,
  };
  return (dispatch, _getState, webApiClient: WebApiClient) => {
    dispatch(
      auditExportToExcel(
        exportPayload,
        dataExportInput.entityName ? dataExportInput.entityName : getSequenceFeatureTypeDisplayName(tableSpec),
      ),
    );
    return webApiClient.downloadFileAndWatchCompletion("/tableexport", exportPayload);
  };
}

function getSortedSelectedIds(table: ITableData): TGridSelModelIdentifier[] {
  return sortBy((selectedId: number) => table.rowIds.indexOf(selectedId))(table.selectionModel.selectedIds as number[]);
}

function getExportPayloads(dataExportInput: IDataExportInput) {
  // TQS TODO consolidate this input object
  const exportPayload: ISequenceExportInput = {
    extension: dataExportInput.format,
    name: dataExportInput.filename,
    headerFormat: dataExportInput.fastaHeaderTemplate,
    compressed: dataExportInput.compressed,
    reverseComplement: dataExportInput.reverseComplement,
    tableQuerySpec: null,
    entityType: null,
    selectedIds: null,
    request: null,
    id: null,
    region: null,
    maxLength: null,
  };

  const exportAsFilePayload: IExportAsFile = {
    extension: dataExportInput.format,
  };

  const tableIsPresent = !!dataExportInput.table;
  const tableSpec = tableIsPresent ? dataExportInput.table.tableQuerySpec : null;
  const tableQuerySpec: InputTableQuerySpec = tableIsPresent ? getTableQuerySpec(dataExportInput, tableSpec) : null;

  let auditPhrase = ""; // describe the exported data
  if (
    dataExportInput.selectionType === SelectionType.SelectedRegion ||
    dataExportInput.selectionType === SelectionType.VisibleRegion
  ) {
    exportPayload.entityType = ENTITY_TYPE.CONTIG;
    exportPayload.selectedIds = null;
    exportPayload.region = dataExportInput.region;
    exportPayload.tableQuerySpec = null;
    exportAsFilePayload.name = "region_export";
    auditPhrase =
      "sequence of " +
      (dataExportInput.selectionType === SelectionType.SelectedRegion ? "selected " : "visible ") +
      "contig region";
  } else if (dataExportInput.selectionType === SelectionType.SelectedRows) {
    exportPayload.entityType = dataExportInput.table.tableQuerySpec.resultType;
    exportPayload.selectedIds = getSortedSelectedIds(dataExportInput.table) as number[];
    exportPayload.region = null;
    exportPayload.tableQuerySpec = tableQuerySpec;

    exportAsFilePayload.name = `selected_${getSequenceFeatureTypeDisplayName(
      dataExportInput.table.tableQuerySpec,
    )}_export`;
    auditPhrase = `selected ${exportPayload.selectedIds.length} ${pluralizeWord(
      "sequence",
      exportPayload.selectedIds.length,
    )}`;
  } else if (dataExportInput.selectionType === SelectionType.AllRows) {
    if (dataExportInput.summaryPageEntity) {
      exportPayload.entityType = dataExportInput.summaryPageEntity.groupType;
      exportPayload.selectedIds = [dataExportInput.summaryPageEntity.id];
      exportPayload.region = null;
      exportPayload.tableQuerySpec = tableQuerySpec;
      exportAsFilePayload.name = `summary_${FEATURE_DISPLAY_NAME[dataExportInput.summaryPageEntity.groupType]}_export`;
      auditPhrase = `sequence`;
    } else {
      exportPayload.entityType = dataExportInput.table.tableQuerySpec.resultType;
      exportPayload.selectedIds = null;
      exportPayload.region = null;
      exportPayload.tableQuerySpec = dataExportInput.table.tableQuerySpec;
      exportAsFilePayload.name = `full_${getSequenceFeatureTypeDisplayName(
        dataExportInput.table.tableQuerySpec,
      )}_export`;
      auditPhrase = `${dataExportInput.table.rowIds.length} ${pluralizeWord(
        "sequence",
        dataExportInput.table.rowIds.length,
      )}`;
    }
  }

  // The following is required if the column-based FASTA header is exported,
  // but needs to be omitted when exporting a region because we do not have a table then.
  if (tableIsPresent) {
    const visibleColumns: Column[] = getVisibleColumns(dataExportInput);
    exportPayload.request = getRequest(visibleColumns, exportPayload.tableQuerySpec);
    exportPayload.columnFields = visibleColumns.map((c) => c.field);
    exportPayload.columnTitles = visibleColumns.map((c) => c.name);
    exportPayload.columnTitleGroups = visibleColumns.map((c) => c.groupName);
    exportPayload.rowCount = getRowCount(dataExportInput);
  }

  if (dataExportInput.toClipboard) {
    exportPayload.maxLength = MAX_SEQUENCE_EXPORT_LENGTH;
  }

  return [exportPayload, exportAsFilePayload, auditPhrase];
}

export function exportSequences(dataExportInput: IDataExportInput): ReduxAction<any> {
  const kindOfSelection = SelectionType2String[dataExportInput.selectionType];
  const [exportPayload, exportAsFilePayload, auditPhrase] = getExportPayloads(dataExportInput);

  return (dispatch, _getState, webApiClient) => {
    if (dataExportInput.toClipboard) {
      dispatch(auditExportSequenceToClipboard(exportPayload, kindOfSelection, auditPhrase));
      return webApiClient.getFileContent("/sequenceexport", exportPayload).catch(
        createErrorHandler(dispatch, {
          actionType: DOWNLOAD_FILE_FAILED,
          showAlert: false,
          throwError: true,
        }),
      );
    } else {
      dispatch(auditExportSequenceFile(exportPayload, exportAsFilePayload, kindOfSelection, auditPhrase));
      return webApiClient.downloadFileAndWatchCompletion("/sequencedownload", exportPayload);
    }
  };
}

export function downloadLogArchive() {
  return (dispatch, getState, webApiClient: WebApiClient) => {
    const appState: IAppState = getState();
    const logArchiveInput: any = {
      clientState: { user: appState.auth.session, route: window.location },
      fullAppState: appState,
      clientSetup: getClientSetup(),
      clientErrors: { stateErrors: appState.errors, loggedErrors: loggedErrors },
    };
    return webApiClient.downloadFileAndWatchCompletion("/logArchive", logArchiveInput).catch(
      createErrorHandler(dispatch, {
        actionType: DOWNLOAD_FILE_FAILED,
        alertTitle: "Failed to Download the Log Archive",
      }),
    );
  };
}

export function downloadProcessorLogs(id: PlaybookEvent["id"]) {
  return (dispatch: ReduxDispatch, _getState: () => IAppState, webApiClient: WebApiClient) => {
    return webApiClient.downloadFileAndWatchCompletion(`/playbookExecutionLogs/${id}`, { id }).catch(
      createErrorHandler(dispatch, {
        actionType: DOWNLOAD_FILE_FAILED,
        alertTitle: "Failed to Download the Processor logs. (FIXME: Not available)",
      }),
    );
  };
}

export function downloadSystemAuditReport(startDate: string, endDate: string) {
  return (_dispatch, _getState, webApiClient: WebApiClient) => {
    const systemAuditReportInput: any = {
      startDate: startDate,
      endDate: endDate,
    };
    return webApiClient.downloadFileAndWatchCompletion("/systemAuditReport", systemAuditReportInput);
  };
}

export function exportItemsOrRegion(payload: IGenericExportPayload) {
  switch (payload.format) {
    case EXPORT_FILE_FORMATS.GFF_FORMAT:
    case EXPORT_FILE_FORMATS.GBK_FORMAT:
      return exportGff(payload as IGffPayload);
    case EXPORT_FILE_FORMATS.VCF_FORMAT:
      return exportVcf(payload as IVcfPayload);
    case EXPORT_FILE_FORMATS.XLSX_FORMAT:
    case EXPORT_FILE_FORMATS.GDC_FORMAT:
      return exportExcelFile(payload as IDataExportInput);
    default:
      return exportSequences(payload as IDataExportInput);
  }
}

function exportGff(payload: IGffPayload) {
  return (dispatch, _getState: () => IAppState, webApiClient: WebApiClient) => {
    dispatch(auditExportGffFile(payload, `genome ${payload.genome.name}`));
    return webApiClient.downloadFileAndWatchCompletion("/genomicexport", {
      format: payload.format,
      trackTypes: payload.trackTypes,
      name: payload.name,
      withSequence: payload.withSequence,
      useChromosomeName: payload.useChromosomeName,
      orgId: payload.genome.id,
      compressed: payload.compressed,
      region: payload.region,
    });
  };
}

function exportVcf(payload: IVcfPayload) {
  // use the same logic for payload creation like for sequence export, but skip the audit phrase because it's not applicable for variant export
  const [exportPayload, _exportAsFilePayload, _auditPhrase] = getExportPayloads(payload);

  return (dispatch, _getState, webApiClient: WebApiClient) => {
    // simplified logic like for gff export, and specific audit phrase including the variant's genome
    dispatch(auditExportVcfFile(payload, payload.genome ? `genome ${payload.genome.name}` : "variant group"));
    return webApiClient.downloadFileAndWatchCompletion("/variantdownload", exportPayload);
  };
}

export function downloadPreparedFile(fileKey: string) {
  return (dispatch, _getState, webApiClient: WebApiClient) => {
    return webApiClient.downloadFileAndWatchCompletion(`/downloadPreparedFile/${fileKey}`, null).catch(
      createErrorHandler(dispatch, {
        actionType: DOWNLOAD_FILE_FAILED,
        showAlert: false,
        throwError: true,
      }),
    );
  };
}
function auditExportSequenceFile(
  exportPayload: ISequenceExportInput,
  exportAsFile: IExportAsFile,
  kindOfSelection: string,
  auditPhrase: string,
) {
  return (_dispatch, _getState, webApiClient: WebApiClient) => {
    let logMessage = "Export ";
    logMessage += `${auditPhrase} to file "${exportAsFile.name}.${exportAsFile.extension}"`;
    const payload = { kindOfSelection, ...exportPayload, ...exportAsFile } as any;

    const log: IAuditLogPayload = {
      type: LOG_ACTION.EXPORT,
      message: logMessage,
      payload: payload,
    };
    webApiClient.auditLog(log);
  };
}

function auditExportGffFile(gffPayload: IGffPayload, auditPhrase: string) {
  return (_dispatch, _getState, webApiClient: WebApiClient) => {
    let logMessage = "Export ";
    logMessage += `${auditPhrase} to file${gffPayload.name ? ` "${gffPayload.name}.gff"` : ""}`;
    const payload = { ...gffPayload, genome: gffPayload.genome.name } as any;

    const log: IAuditLogPayload = {
      type: LOG_ACTION.EXPORT,
      message: logMessage,
      payload: payload,
    };
    webApiClient.auditLog(log);
  };
}

function auditExportVcfFile(vcfPayload: IVcfPayload, auditPhrase: string) {
  return (_dispatch, _getState, webApiClient: WebApiClient) => {
    let logMessage = "Export ";
    logMessage += `${auditPhrase} to file${vcfPayload.filename ? ` "${vcfPayload.filename}.vcf"` : ""}`;
    const payload = { ...vcfPayload, genome: vcfPayload.genome?.name };

    const log: IAuditLogPayload = {
      type: LOG_ACTION.EXPORT,
      message: logMessage,
      payload: payload,
    };
    webApiClient.auditLog(log);
  };
}
function auditExportSequenceToClipboard(
  exportPayload: ISequenceExportInput,
  kindOfSelection: string,
  auditPhrase: string,
) {
  return (_dispatch, _getState, webApiClient: WebApiClient) => {
    const logMessage = `Export ${auditPhrase} to clipboard`;
    const payload = { kindOfSelection, ...exportPayload } as any;

    const log: IAuditLogPayload = {
      type: LOG_ACTION.EXPORT,
      message: logMessage,
      payload: payload,
    };
    webApiClient.auditLog(log);
  };
}

function auditExportToExcel(exportPayload: ITableDownloadFileParams, entityName: string) {
  return (_dispatch, _getState, webApiClient: WebApiClient) => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { request, columnTitles, columnTitleGroups, columnFields, ...payload } = exportPayload;

    let auditPhrase: string;
    if (exportPayload.selectedIds && exportPayload.selectedIds.length > 0) {
      auditPhrase = `${exportPayload.selectedIds.length} ${pluralizeWord(
        entityName,
        exportPayload.selectedIds.length,
      )}`;
    } else if (exportPayload.rowCount > 0) {
      auditPhrase = `${exportPayload.rowCount} ${pluralizeWord(entityName, exportPayload.rowCount)}`;
    } else {
      auditPhrase = pluralize.plural(entityName);
    }

    const logMessage = `Export ${auditPhrase} as excel file "${exportPayload.name}.${exportPayload.extension}"`;

    const log: IAuditLogPayload = {
      type: LOG_ACTION.EXPORT,
      message: logMessage,
      payload: payload,
    };
    webApiClient.auditLog(log);
  };
}

function auditExportFromPixelator(payload: any) {
  return (_dispatch, _getState, webApiClient: WebApiClient) => {
    const logMessage = "Export a chart from Analytical Data Viewer";

    const log: IAuditLogPayload = {
      type: LOG_ACTION.EXPORT,
      message: logMessage,
      payload: payload,
    };
    webApiClient.auditLog(log);
  };
}
